/* Copyright (c) 2019-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, NgZone } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { BehaviorSubject, from, fromEvent, merge, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, map, mergeMap, startWith, tap } from 'rxjs/operators';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { PlatformDetectorService } from 'crimson/lib/platform-detector/platform-detector.service';
import {
    FullscreenSources,
    transparentImgHref,
    HostNames,
    RegxReducePathnameToBranch,
    BrowserPermissionName
} from './constants';

export type ApplicationWindowState = 'foreground' | 'background';

@Injectable({
    providedIn: 'root'
})
export class BrowserSupportService {
    private readonly keyCodes = {
        c: 'KeyC',
        i: 'KeyI',
        j: 'KeyJ',
        u: 'KeyU',
        f12: 'F12'
    };
    private readonly logger: Logger;
    private readonly isPWAModeOnLGTV = PlatformDetectorService.isPWAModeOnLGTV();
    private readonly isPWAModeOnSamsungTV = PlatformDetectorService.isPWAModeOnSamsungTV();
    // This Subject is used to transfer data from Component to Plugin
    // TODO: Need to look for better design
    private fullScreenEvent: Subject<boolean> = new BehaviorSubject<boolean>(false);
    private fullScreenSource: FullscreenSources;
    private appIcon: HTMLLinkElement;
    private appIconHref: string;
    private hostBranch: string;
    private nativeWindowStateSubject = new BehaviorSubject<ApplicationWindowState>('foreground');
    private useNativeWindowStateEvents = false;
    /**
     * Per-permission state to assume when the W3C Permissions API is unavailable
     * in the runtime (e.g. the Vega OS / Fire TV WebView). Configured once by the
     * host app via {@link initialize}; permissions absent from the map default to
     * 'denied' so a missing Permissions API is never mistaken for a grant.
     */
    private permissionFallbackStates: Partial<Record<BrowserPermissionName, PermissionState>> = {};

    public get fullScreenEventSubject(): Subject<boolean> {
        return this.fullScreenEvent;
    }

    public get fullScreenEventObs$(): Observable<boolean> {
        return this.fullScreenEvent.asObservable();
    }

    public get documentVisiblityChange(): Observable<any> {
        return fromEvent(document, 'visibilitychange');
    }

    resetExitPath(): void {
        this.fullScreenSource = undefined;
    }

    setExitPath(source: FullscreenSources): void {
        this.fullScreenSource = source;
    }

    public get resumeFullScreenIGO(): boolean {
        return this.fullScreenSource === FullscreenSources.IGO;
    }

    get applicationWindowState$(): Observable<ApplicationWindowState> {
        return this.isPWAModeOnLGTV || this.useNativeWindowStateEvents
            ? this.nativeWindowStateSubject.asObservable()
            : this.documentVisiblityChange.pipe(
                  startWith(0),
                  map<any, ApplicationWindowState>(_ =>
                      document.visibilityState === 'visible' ? 'foreground' : 'background'
                  ),
                  distinctUntilChanged()
              );
    }

    constructor(
        loggingService: LoggingService,
        private titleService: Title,
        private ngZone: NgZone
    ) {
        this.logger = loggingService.getLogger('gfn/browser-support');
        this.appIcon = document.querySelector('#appIcon');
        this.appIconHref = this.appIcon?.href;
        this.hostBranch = window.location.pathname.replace(RegxReducePathnameToBranch, '');
    }

    initialize(
        addDummyListener: boolean,
        enableDevTools: boolean,
        permissionFallbackStates?: Partial<Record<BrowserPermissionName, PermissionState>>,
        useNativeWindowStateEvents = false
    ) {
        this.permissionFallbackStates = permissionFallbackStates ?? {};
        this.useNativeWindowStateEvents = useNativeWindowStateEvents;
        /**
         * There's an issue with mouse-wheel in Chrome:
         * https://support.google.com/chrome/forum/AAAAP1KN0B0bHCWBm7v9c4
         * Scrolling can stop working randomly in streamer. Surprisingly,
         * adding a dummy wheel listener in Mall can prevent it.
         * Accordingly, this WAR was added for Bug 2851739.
         */
        if (addDummyListener) {
            this.ngZone.runOutsideAngular(() => {
                document.addEventListener('wheel', this.dummyWheelListener, { passive: true });
            });
        }
        if (!enableDevTools) {
            this.disableDevTools();
        }

        if (this.isPWAModeOnLGTV) {
            this.ngZone.runOutsideAngular(() => {
                // windowStateChanged is a webOS specific event
                window.addEventListener('windowStateChanged', this.webOSWindowStateListener);
            });
        }
    }

    /**
     * Pushes a native foreground/background transition into the shared window-state
     * stream. Platform integrations (e.g. the Vega RN shell via VegaHelperService)
     * call this so this service stays unaware of platform-specific transports.
     */
    notifyWindowStateChange(state: ApplicationWindowState): void {
        if (this.nativeWindowStateSubject.value !== state) {
            this.nativeWindowStateSubject.next(state);
        }
    }

    private dummyWheelListener(e: WheelEvent) {
        return e;
    }

    private webOSWindowStateListener = (evt: Event) => {
        this.notifyWindowStateChange((evt as any).detail.state);
    };

    /**
     * Disable context menu in browser.
     *
     * Will also disable the following context menu shortcuts
     * - Windows:
     *   - CTRL + SHIFT + I
     *   - CTRL + SHIFT + J
     *   - CTRL + U
     *   - F12
     * - Mac
     *   - SHIFT + COMMAND + C
     *   - OPTION + COMMAND + I
     *   - OPTION + COMMAND + J
     *   - OPTION + COMMAND + U
     */
    private disableDevTools() {
        this.ngZone.runOutsideAngular(() => {
            document.addEventListener('contextmenu', event => event.preventDefault());
            window.addEventListener('keydown', event => {
                if (
                    (event.code === this.keyCodes.i && event.ctrlKey && event.shiftKey) ||
                    (event.code === this.keyCodes.j && event.ctrlKey && event.shiftKey) ||
                    (event.code === this.keyCodes.u && event.ctrlKey) ||
                    event.code === this.keyCodes.f12 ||
                    (event.code === this.keyCodes.c && event.shiftKey && event.metaKey) ||
                    (event.code === this.keyCodes.i && event.altKey && event.metaKey) ||
                    (event.code === this.keyCodes.j && event.altKey && event.metaKey) ||
                    (event.code === this.keyCodes.u && event.altKey && event.metaKey)
                ) {
                    event.preventDefault();
                }
            });
        });
    }

    getGamepads(): Gamepad[] {
        if (!navigator.getGamepads) {
            return [];
        }

        const gamepads = navigator.getGamepads();
        if (gamepads instanceof Array) {
            return gamepads;
        } else {
            // Some browsers return GamepadList
            const gamepadList = gamepads as ArrayLike<Gamepad>;
            return Array.from(gamepadList).filter(g => !!g);
        }
    }

    goToPreviousPage() {
        window.history.back();
    }

    supportsRequestFullscreen(element: HTMLElement): boolean {
        return (
            (document.fullscreenEnabled && element.requestFullscreen) ||
            ((document as any).webkitFullscreenEnabled && (element as any).webkitRequestFullscreen)
        );
    }

    /**
     * Tests if the web page is being displayed in fullscreen.
     */
    isFullscreen(): boolean {
        // Handles the case that browser itself is in "borderless" fullscreen mode (e.g. Chrome + F11 key).
        const isBorderLessFullScreen = screen.width === window.innerWidth && screen.height === window.innerHeight;
        const isPWAModeOnTV = this.isPWAModeOnLGTV || this.isPWAModeOnSamsungTV;
        return (
            document.fullscreenElement ||
            (document as any).webkitFullscreenElement ||
            (!isPWAModeOnTV && isBorderLessFullScreen)
        );
    }

    requestFullscreen(element: HTMLElement): void {
        if (!this.supportsRequestFullscreen(element)) {
            this.logger.error('Fullscreen not supported');
            return;
        }

        if (element.requestFullscreen) {
            const fsPromise = element.requestFullscreen({ keyboardLock: 'browser' } as any);
            if (fsPromise) {
                fsPromise.catch(err => {
                    this.logger.error(`Error attempting to enable fullscreen mode: ${err.message} (${err.name})`);
                });
            } else {
                this.logger.info('Fullscreen request API did not return a promise');
            }
        } else if ((element as any).webkitRequestFullscreen) {
            (element as any).webkitRequestFullscreen({ keyboardLock: 'browser' } as any);
        } else {
            this.logger.error('No available method to enter fullscreen');
        }
    }

    exitFullscreen(): void {
        // At times when we try to exitFullscreen, the document mat not be active
        // Hence checking for the existence of fullscreenElement before trying to exit
        // fullscreen mode.
        if (document.fullscreenElement && document.exitFullscreen) {
            document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
            (document as any).webkitExitFullscreen();
        } else {
            this.logger.error('No available method to exit fullscreen');
        }
    }

    // TODO(MVPC-7057, AVENGERS-1447): remove this and use configs once we have separate configs in staging builds
    isStagingOrDevDomain() {
        return this.isStagingDomain() || this.isLocalDomain();
    }

    isStagingDomain() {
        return window.location.hostname.endsWith(HostNames.Stage);
    }

    isPartnerDomain() {
        return window.location.hostname.endsWith(HostNames.Partner);
    }

    isLocalDomain() {
        return window.location.hostname.endsWith(HostNames.Local);
    }

    /**
     * Return Web hosted Origin Path.
     */
    getHostBranchName() {
        return this.hostBranch;
    }

    /**
     * Sets title text of the Page.
     */
    setTitle(newTitle: string) {
        if (newTitle) {
            this.titleService.setTitle(newTitle);
        }
    }

    /**
     * Toggles between logo image and transparent image to give blinking effect.
     */
    blinkAppIcon(timeLeft: number): void {
        if (this.appIcon && this.appIconHref) {
            if (timeLeft % 2 === 1) {
                this.appIcon.href = transparentImgHref;
            } else {
                this.resetAppIcon();
            }
        }
    }

    /**
     * Resets the appIcon image
     */
    resetAppIcon(): void {
        if (this.appIconHref) {
            this.appIcon.href = this.appIconHref;
        }
    }

    cleanup() {
        document.removeEventListener('wheel', this.dummyWheelListener);
        this.resetAppIcon();

        if (this.isPWAModeOnLGTV) {
            window.removeEventListener('windowStateChanged', this.webOSWindowStateListener);
        }
    }

    /**
     * Resolves the state of a browser permission. Callers stay agnostic of how
     * the lookup is handled: when the runtime exposes the W3C Permissions API the
     * live state is returned; otherwise (e.g. the Vega OS / Fire TV WebView, where
     * querying would throw synchronously and stall the permission flow) the
     * configured per-platform fallback from {@link initialize} is used, defaulting
     * to the conservative 'denied' — a resolved (non-'prompt') state so
     * permission-gated flows do not hang and access is never wrongly assumed.
     */
    getPermission$(name: BrowserPermissionName): Observable<PermissionState> {
        if (!navigator.permissions?.query) {
            const fallbackState = this.permissionFallbackStates[name] ?? 'denied';
            this.logger.info(
                `BrowserPermission: ${name} - Permissions API unavailable; defaulting to '${fallbackState}'`
            );
            return of(fallbackState);
        }
        return from(navigator.permissions.query({ name: name as PermissionName })).pipe(
            mergeMap(permission =>
                merge(
                    of(permission.state),
                    fromEvent(permission, 'change').pipe(map(event => (event.target as PermissionStatus).state))
                )
            ),
            tap(val => this.logger.info(`BrowserPermission: ${name} value: ${val}`))
        );
    }
}
